import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Settings, AlertTriangle, FlaskConical, CircleSlash, Zap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchOrderingConfig, updateOrderingConfig } from '@/hooks/useAdmin';
import type { OrderingConfigDto } from '@uniticket/shared';

type Mode = OrderingConfigDto['mode'];

const MODE_BANNER: Record<Mode, { Icon: typeof Zap; cls: string }> = {
  LIVE: { Icon: Zap, cls: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  DRY_RUN: { Icon: FlaskConical, cls: 'border-blue-200 bg-blue-50 text-blue-800' },
  OFF: { Icon: CircleSlash, cls: 'border-slate-200 bg-slate-50 text-slate-700' },
};

export function OrderingConfigPage() {
  const { t } = useTranslation('admin');
  const [config, setConfig] = useState<OrderingConfigDto | null>(null);
  const [mode, setMode] = useState<Mode>('DRY_RUN');
  const [timeoutSeconds, setTimeoutSeconds] = useState('15');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetchOrderingConfig();
      setConfig(res.data);
      setMode(res.data.mode);
      setTimeoutSeconds(String(res.data.timeoutSeconds));
    } catch (err: any) {
      toast.error(err?.message || t('orderingConfig.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const secs = Number(timeoutSeconds);
    if (!Number.isInteger(secs) || secs < 1 || secs > 120) {
      toast.error(t('orderingConfig.timeoutInvalid'));
      return;
    }
    setSaving(true);
    try {
      const res = await updateOrderingConfig({ mode, timeoutSeconds: secs });
      setConfig(res.data);
      setMode(res.data.mode);
      setTimeoutSeconds(String(res.data.timeoutSeconds));
      toast.success(t('orderingConfig.saved'));
    } catch (err: any) {
      toast.error(err?.message || t('orderingConfig.saveError'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-72 w-full max-w-2xl" />
      </div>
    );
  }

  const liveLocked = config?.forceDryRun === true; // env failsafe pins DRY_RUN
  const notConfigured = config?.enabled === false; // no per-operator creds
  const currentMode = config?.mode ?? 'DRY_RUN';
  const banner = MODE_BANNER[currentMode];
  const BannerIcon = banner.Icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <Settings className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('orderingConfig.title')}</h2>
          <p className="text-sm text-slate-500">{t('orderingConfig.description')}</p>
        </div>
      </div>

      {/* Current effective-mode banner */}
      <div className={`max-w-2xl rounded-xl border px-4 py-3 flex items-start gap-3 ${banner.cls}`}>
        <BannerIcon className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="font-semibold">{t(`orderingConfig.banner.${currentMode}`)}</p>
          <p className="opacity-90">{t(`orderingConfig.bannerHint.${currentMode}`)}</p>
        </div>
      </div>

      {notConfigured && (
        <div className="max-w-2xl rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3 text-sm text-amber-800">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <p>{t('orderingConfig.notConfigured')}</p>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
        <form onSubmit={handleSave} className="space-y-6">
          {/* Mode */}
          <div className="space-y-1.5">
            <Label htmlFor="mode">{t('orderingConfig.mode')}</Label>
            <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
              <SelectTrigger id="mode" className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LIVE" disabled={liveLocked}>{t('orderingConfig.modeLive')}</SelectItem>
                <SelectItem value="DRY_RUN">{t('orderingConfig.modeDryRun')}</SelectItem>
                <SelectItem value="OFF">{t('orderingConfig.modeOff')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">{t(`orderingConfig.modeHint.${mode}`)}</p>
            {liveLocked && (
              <p className="text-xs text-blue-700 flex items-center gap-1.5">
                <FlaskConical className="w-3.5 h-3.5" />
                {t('orderingConfig.forceDryRunNotice')}
              </p>
            )}
          </div>

          {/* Timeout */}
          <div className="space-y-1.5">
            <Label htmlFor="timeout">{t('orderingConfig.timeout')}</Label>
            <Input
              id="timeout"
              type="number"
              min={1}
              max={120}
              value={timeoutSeconds}
              onChange={(e) => setTimeoutSeconds(e.target.value)}
              className="max-w-[10rem]"
            />
            <p className="text-xs text-slate-400">{t('orderingConfig.timeoutHint')}</p>
          </div>

          <div className="pt-2">
            <Button type="submit" disabled={saving} className="flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? t('orderingConfig.saving') : t('orderingConfig.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
